/**
 * @file App.js
 * Main entry point for the application, setting up routing and rendering core components.
 */

import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import "./App.css";
import BottomBar from "./Components/BottomBar";
import Navbar from "./Components/Navbar";
import Home from "./Pages/Home";
import Species from "./Pages/Species";
import Login from "./Pages/Login";
import Identify from "./Pages/Identify";
import Team from "./Pages/Team";
import global_en from "./translations/en/global.json";
import global_bn from "./translations/bn/global.json";
import i18next from "i18next";
import { I18nextProvider } from "react-i18next";
import Contact from "./Pages/Contact";
import About from "./Pages/About";
import { useEffect, useState } from "react";
import Tree from "./Pages/Tree";
import Profile from "./Pages/Profile";
import Admin from "./Pages/Admin";
import FAQ from "./Pages/FAQ";
import TermsOfUse from "./Pages/TermsOfUse";
import ApiDocumentation from "./Pages/ApiDocumentation";
import Manual from "./Pages/Manual";
import PrivacyPolicy from "./Pages/PrivacyPolicy";
import DeleteProfile from "./Pages/DeleteProfile";
/**
 * Main content component for the application.
 * Manages application-wide state, including user, language, and tree selection, with i18n setup for language management.
 *
 * @component
 * @returns {JSX.Element} Renders the main content of the app.
 */
function AppContent() {
  const [treeId, setTree] = useState(localStorage.getItem("tree") || "1");
  const [user, setUser] = useState(localStorage.getItem("user") || "");
  const [lang, setlang] = useState(localStorage.getItem("lang") || "en");
  const location = useLocation();

  useEffect(() => {
    localStorage.setItem("user", user);
  }, [user]);

  useEffect(() => {
    localStorage.setItem("lang", lang);
  }, [lang]);

  useEffect(() => {
    localStorage.setItem("tree", treeId);
  }, [treeId]);

  const [faqData, setFaqData] = useState([]);
  /**
   * Fetches FAQ data from a local JSON file and updates state.
   * Logs an error if the fetch fails.
   */
  useEffect(() => {
    fetch("faqData.json")
      .then((response) => response.json())
      .then((data) => setFaqData(data))
      .catch((error) => console.error("Error fetching FAQ data:", error));
  }, []);

  i18next.init({
    interpolation: { escapeValue: false },
    lng: lang,
    resources: {
      en: {
        global: global_en,
      },
      bn: {
        global: global_bn,
      },
    },
  });

  return (
    <div className="bangla-font">
      <I18nextProvider i18n={i18next}>
        {location.pathname !== "/admin" && (
          <Navbar setlangstate={setlang} user={user} />
        )}
        <Routes>
          <Route path="/" element={<Home setTree={setTree} />} />
          <Route path="home" element={<Home setTree={setTree} />} />
          <Route path="species" element={<Species setTree={setTree} />} />
          <Route path="loginuser" element={<Login setUser={setUser} />} />
          <Route path="identify" element={<Identify setTree={setTree} />} />
          <Route path="team" element={<Team />} />
          <Route path="userapi" element={<ApiDocumentation />} />
          <Route path="contact" element={<Contact />} />
          <Route path="about" element={<About />} />
          <Route path="tree" element={<Tree id={treeId} />} />
          <Route path="profile" element={<Profile user={user} />} />
          <Route
            path="deletemyprofile"
            element={<DeleteProfile user={user} />}
          />
          <Route path="tou" element={<TermsOfUse />} />
          <Route path="manual" element={<Manual />} />
          <Route path="privacy" element={<PrivacyPolicy />} />
          <Route
            path="admin"
            element={<Admin faqData={faqData} setFaqData={setFaqData} />}
          />
          <Route path="faq" element={<FAQ faqData={faqData} />} />
          <Route path="*" element={<Home />} /> {/* Catch-all */}
        </Routes>
        {location.pathname !== "/admin" && <BottomBar />}
      </I18nextProvider>
    </div>
  );
}
/**
 * Root component of the app, wraps `AppContent` with `BrowserRouter` for routing functionality.
 *
 * @component
 * @returns {JSX.Element} The root component of the application.
 */
function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
